// 1. Add request throttling to the caching hook
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/features/auth/auth-context';

interface VideoData {
  id: string;
  title?: string;
  description?: string;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  lastUpdated: string | null;
  dataSource: 'cached_fresh' | 'cached_stale' | 'live_fallback';
  syncAge?: number;
  publishedAt?: string;
  thumbnails?: any;
  url?: string;
}

export const useYouTubeDataCached = (videoIds: string[]) => {
  const [data, setData] = useState<Record<string, VideoData>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  
  // CRITICAL: Prevent runaway requests
  const lastVideoIdsRef = useRef<string>('');
  const isLoadingRef = useRef(false);
  const lastSyncRequestRef = useRef<number>(0);
  const syncCooldownRef = useRef<Set<string>>(new Set());

  const videoIdsString = videoIds.sort().join(',');

  useEffect(() => {
    if (!videoIds.length || !user || videoIdsString === lastVideoIdsRef.current || isLoadingRef.current) {
      if (!videoIds.length) {
        setData({});
      }
      return;
    }

    lastVideoIdsRef.current = videoIdsString;
    
    const fetchCachedData = async () => {
      if (isLoadingRef.current) return;
      
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      
      try {
        console.log(`[Cache] Fetching cached data for ${videoIds.length} videos`);
        
        const { data: uploads, error: dbError } = await supabase
          .from('uploads')
          .select(`
            youtube_video_id,
            title,
            description,
            thumbnail_url,
            created_at,
            cached_view_count,
            cached_like_count,
            cached_comment_count,
            last_stats_sync,
            stats_stale,
            sync_priority
          `)
          .in('youtube_video_id', videoIds)
          .eq('user_id', user.id);

        if (dbError) throw dbError;

        const dataMap: Record<string, VideoData> = {};
        const now = new Date();

        uploads?.forEach(upload => {
          if (upload.youtube_video_id) {
            const lastSync = upload.last_stats_sync ? new Date(upload.last_stats_sync) : null;
            const syncAge = lastSync ? Math.round((now.getTime() - lastSync.getTime()) / (1000 * 60)) : null;
            
            let dataSource: VideoData['dataSource'] = 'cached_fresh';
            if (upload.stats_stale || !lastSync) {
              dataSource = 'cached_stale';
            } else if (syncAge && syncAge > 120) {
              dataSource = 'cached_stale';
            }

            dataMap[upload.youtube_video_id] = {
              id: upload.youtube_video_id,
              title: upload.title,
              description: upload.description,
              viewCount: upload.cached_view_count || 0,
              likeCount: upload.cached_like_count || 0,
              commentCount: upload.cached_comment_count || 0,
              lastUpdated: upload.last_stats_sync,
              dataSource,
              syncAge: syncAge || undefined,
              publishedAt: upload.created_at,
              thumbnails: upload.thumbnail_url ? {
                default: { url: upload.thumbnail_url },
                high: { url: upload.thumbnail_url }
              } : {},
              url: `https://youtube.com/watch?v=${upload.youtube_video_id}`
            };
          }
        });

        console.log(`[Cache] Loaded ${Object.keys(dataMap).length}/${videoIds.length} videos from cache`);
        setData(dataMap);
        
      } catch (error: any) {
        console.error('[Cache] Failed to fetch cached stats:', error);
        setError(error.message || 'Failed to load video statistics');
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    fetchCachedData();
  }, [videoIdsString, user?.id]);

  // CRITICAL: Add aggressive throttling and cooldown
  const requestPrioritySync = useCallback(async (videoIdsToSync: string[]) => {
    if (!user || !videoIdsToSync.length) return;

    const now = Date.now();
    const SYNC_COOLDOWN = 30000; // 30 second cooldown per video
    const GLOBAL_COOLDOWN = 10000; // 10 second global cooldown

    // Global cooldown check
    if (now - lastSyncRequestRef.current < GLOBAL_COOLDOWN) {
      console.warn(`[Cache] BLOCKED: Global cooldown active (${Math.round((GLOBAL_COOLDOWN - (now - lastSyncRequestRef.current)) / 1000)}s remaining)`);
      return;
    }

    // Per-video cooldown check
    const blockedVideos = videoIdsToSync.filter(id => syncCooldownRef.current.has(id));
    if (blockedVideos.length > 0) {
      console.warn(`[Cache] BLOCKED: Videos in cooldown:`, blockedVideos);
      return;
    }

    try {
      console.log(`[Cache] Requesting priority sync for ${videoIdsToSync.length} videos`);
      lastSyncRequestRef.current = now;

      // Add videos to cooldown
      videoIdsToSync.forEach(id => {
        syncCooldownRef.current.add(id);
        setTimeout(() => {
          syncCooldownRef.current.delete(id);
        }, SYNC_COOLDOWN);
      });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      const backendUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5002";
      const response = await fetch(`${backendUrl}/youtube-sync-priority`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          videoIds: videoIdsToSync,
          priority: 'high'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to request priority sync');
      }

      const result = await response.json();
      console.log(`[Cache] Priority sync requested:`, result);

      // Update local state
      setData(prev => {
        const updated = { ...prev };
        videoIdsToSync.forEach(id => {
          if (updated[id]) {
            updated[id] = {
              ...updated[id],
              dataSource: 'cached_stale'
            };
          }
        });
        return updated;
      });

    } catch (error: any) {
      console.error('[Cache] Failed to request priority sync:', error);
      // Remove from cooldown on error
      videoIdsToSync.forEach(id => syncCooldownRef.current.delete(id));
      throw error;
    }
  }, [user]);

  return { 
    youtubeDataMap: data, 
    loading,
    error,
    requestPrioritySync,
    // Utility functions
    getDataAge: useCallback((videoId: string) => data[videoId]?.syncAge, [data]),
    isDataStale: useCallback((videoId: string) => {
      const videoData = data[videoId];
      return videoData?.dataSource === 'cached_stale' || !videoData?.lastUpdated;
    }, [data]),
    hasData: useCallback((videoId: string) => !!data[videoId], [data]),
    totalVideos: Object.keys(data).length,
  };
};